import React, { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { CandidateWithSlides, Evaluator } from '../../lib/supabase';
import { UNIVERSITIES, getUniversity, type UniversityId } from '../../lib/constants';
import EvaluationDetail from '../Evaluator/EvaluationDetail';
import UniversityColumn from './UniversityColumn';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';

export default function UniversityDashboard() {
  const [candidates, setCandidates] = useState<CandidateWithSlides[]>([]);
  const [evaluator, setEvaluator] = useState<Evaluator | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateWithSlides | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [likeCounts, setLikeCounts] = useState<Record<string, number>>({});
  const [participateCounts, setParticipateCounts] = useState<Record<string, number>>({});
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({});
  const [avgRatings, setAvgRatings] = useState<Record<string, number>>({});
  const [ratingCounts, setRatingCounts] = useState<Record<string, number>>({});
  const [ownCandidateId, setOwnCandidateId] = useState<string | null>(null);
  const [mutedCandidates, setMutedCandidates] = useState<Set<string>>(new Set());
  const [userEmail, setUserEmail] = useState<string | null>(null);
  // Mobile tab
  const [activeTab, setActiveTab] = useState<UniversityId>('gunma');

  const cancelledRef = React.useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    loadData();
    return () => { cancelledRef.current = true; };
  }, []);

  const loadData = async () => {
    const isCancelled = () => cancelledRef.current;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (isCancelled() || !user) return;
      setUserEmail(user.email || null);

      // Load muted candidates
      try {
        const { data: mutes } = await supabase.from('notification_mutes').select('candidate_id').eq('user_email', user.email);
        if (isCancelled()) return;
        setMutedCandidates(new Set((mutes || []).map((m: any) => m.candidate_id)));
      } catch {}

      // Load or create evaluator
      let evalData: Evaluator | null = null;
      {
        const { data } = await supabase.from('evaluators').select('*').eq('email', user.email).maybeSingle();
        if (isCancelled()) return;
        evalData = data as Evaluator | null;
      }
      if (!evalData) {
        const { data: cand } = await supabase.from('candidates').select('*').eq('email', user.email).maybeSingle();
        if (isCancelled()) return;
        if (cand) {
          await supabase.from('evaluators').insert({
            name: cand.name, email: cand.email,
            university: cand.university || null,
            affiliation: cand.affiliation, department: cand.department || null,
            is_grad_student: cand.is_grad_student || false,
            grad_program: cand.grad_program || null, grad_year: cand.grad_year || null
          });
          if (isCancelled()) return;
          const { data } = await supabase.from('evaluators').select('*').eq('email', user.email).single();
          if (isCancelled()) return;
          evalData = data as Evaluator;
        }
      }
      setEvaluator(evalData);

      // Own candidate id
      const { data: ownCand } = await supabase.from('candidates').select('id').eq('email', user.email).maybeSingle();
      if (isCancelled()) return;
      if (ownCand?.id) setOwnCandidateId(ownCand.id);

      // Load candidates with slides
      const { data: candidatesData, error: cErr } = await supabase
        .from('candidates')
        .select('*, slide_images (*)')
        .order('created_at', { ascending: false });
      if (isCancelled()) return;
      if (cErr) throw cErr;
      setCandidates(candidatesData || []);

      // Like counts
      try {
        const { data: likesData } = await supabase.from('evaluations').select('candidate_id').eq('liked', true);
        if (isCancelled()) return;
        const counts: Record<string, number> = {};
        (likesData || []).forEach((r: any) => { counts[r.candidate_id] = (counts[r.candidate_id] || 0) + 1; });
        setLikeCounts(counts);
      } catch {}

      // Participate counts
      try {
        const { data: partsData } = await supabase.from('evaluations').select('candidate_id').eq('want_to_participate', true);
        if (isCancelled()) return;
        const counts: Record<string, number> = {};
        (partsData || []).forEach((r: any) => { counts[r.candidate_id] = (counts[r.candidate_id] || 0) + 1; });
        setParticipateCounts(counts);
      } catch {}

      // Comment counts + rating averages
      try {
        const { data: evals } = await supabase.from('evaluations').select('id, candidate_id, rating');
        if (isCancelled()) return;
        const evalToCandidate = new Map<string, string>();
        (evals || []).forEach((e: any) => evalToCandidate.set(e.id, e.candidate_id));

        const { data: comments } = await supabase.from('evaluation_comments').select('evaluation_id');
        if (isCancelled()) return;
        const cCounts: Record<string, number> = {};
        (comments || []).forEach((r: any) => {
          const cid = evalToCandidate.get(r.evaluation_id);
          if (cid) cCounts[cid] = (cCounts[cid] || 0) + 1;
        });
        setCommentCounts(cCounts);

        const rCounts: Record<string, number> = {};
        const rSums: Record<string, number> = {};
        (evals || []).forEach((e: any) => {
          const cid = e.candidate_id as string;
          const r = Math.max(0, Math.min(5, e.rating || 0));
          if (r > 0) {
            rCounts[cid] = (rCounts[cid] || 0) + 1;
            rSums[cid] = (rSums[cid] || 0) + r;
          }
        });
        const rAvg: Record<string, number> = {};
        Object.keys(rSums).forEach(cid => {
          rAvg[cid] = rCounts[cid] > 0 ? rSums[cid] / rCounts[cid] : 0;
        });
        setRatingCounts(rCounts);
        setAvgRatings(rAvg);
      } catch {}
    } catch (err: any) {
      if (!isCancelled()) setError(err.message);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  const toggleMute = async (candidateId: string) => {
    if (!userEmail) return;
    const isMuted = mutedCandidates.has(candidateId);
    if (isMuted) {
      await supabase.from('notification_mutes').delete().eq('user_email', userEmail).eq('candidate_id', candidateId);
      setMutedCandidates(prev => { const s = new Set(prev); s.delete(candidateId); return s; });
    } else {
      await supabase.from('notification_mutes').insert({ user_email: userEmail, candidate_id: candidateId });
      setMutedCandidates(prev => new Set(prev).add(candidateId));
    }
  };

  const filtered = searchTerm.trim()
    ? candidates.filter((c) => {
        const s = searchTerm.toLowerCase();
        const uni = getUniversity(c.university);
        return c.name.toLowerCase().includes(s) || c.affiliation.toLowerCase().includes(s) ||
          c.research_topic.toLowerCase().includes(s) || c.research_overview.toLowerCase().includes(s) ||
          (c as any).research_comment?.toLowerCase().includes(s) ||
          uni.name.toLowerCase().includes(s) ||
          c.department?.toLowerCase().includes(s);
      })
    : candidates;

  const groupByUniversity = (list: CandidateWithSlides[]) => {
    const groups: Record<string, CandidateWithSlides[]> = { tsukuba: [], chiba: [], gunma: [] };
    list.forEach((c) => {
      const key = c.university || 'tsukuba';
      if (groups[key]) groups[key].push(c);
    });
    return groups;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (selectedCandidate && evaluator) {
    return (
      <EvaluationDetail
        candidate={selectedCandidate}
        evaluator={evaluator}
        isOwn={ownCandidateId === selectedCandidate.id}
        onBack={() => { setSelectedCandidate(null); loadData(); }}
        onEvaluationSubmitted={loadData}
      />
    );
  }

  const groups = groupByUniversity(filtered);

  return (
    <div className="space-y-6">
      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      <div>
        <h1 className="text-2xl font-bold text-foreground">ToRA-SEED ダッシュボード</h1>
        <p className="text-muted-foreground mt-1">
          {filtered.length} 件の研究発表
          {searchTerm && ` (全${candidates.length}件中)`}
        </p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="研究者名・大学名・所属・研究課題で検索..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchTerm && (
              <Button variant="ghost" size="sm" className="absolute right-1 top-1 h-8 w-8 p-0" onClick={() => setSearchTerm('')}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Desktop: 3 columns */}
      <div className="hidden md:grid md:grid-cols-3 gap-4">
        {UNIVERSITIES.map((uni) => (
          <UniversityColumn
            key={uni.id}
            universityId={uni.id}
            candidates={groups[uni.id] || []}
            likeCounts={likeCounts}
            commentCounts={commentCounts}
            participateCounts={participateCounts}
            avgRatings={avgRatings}
            ratingCounts={ratingCounts}
            mutedCandidates={mutedCandidates}
            onToggleMute={toggleMute}
            onSelectCandidate={setSelectedCandidate}
          />
        ))}
      </div>

      {/* Mobile: Tabs */}
      <div className="md:hidden space-y-4">
        <div className="flex rounded-lg border overflow-hidden">
          {UNIVERSITIES.map((uni) => (
            <button
              key={uni.id}
              onClick={() => setActiveTab(uni.id)}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                activeTab === uni.id
                  ? `${uni.color} text-white`
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {uni.name.replace('大学', '')}
              <span className="ml-1 text-xs">({(groups[uni.id] || []).length})</span>
            </button>
          ))}
        </div>

        <UniversityColumn
          universityId={activeTab}
          candidates={groups[activeTab] || []}
          likeCounts={likeCounts}
          commentCounts={commentCounts}
          participateCounts={participateCounts}
          avgRatings={avgRatings}
          ratingCounts={ratingCounts}
          onSelectCandidate={setSelectedCandidate}
        />
      </div>
    </div>
  );
}
